import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { rescheduleJob } from "../api/calendar.ts";
import {
  addJobNote,
  assignJob,
  fetchJobAttachmentBlobUrl,
  getJob,
  listJobAttachments,
  mintJobMagicLink,
  notifyTech,
  revokeJobMagicLink,
  sendTestNotification,
  transitionJob,
  updateJob,
  type JobAttachment,
  type JobStatus,
  type TestEvent,
  type TestSendResult,
} from "../api/jobs.ts";
import { updateClient } from "../api/clients.ts";
import { updateLead } from "../api/leads.ts";
import { listTechnicians } from "../api/technicians.ts";

type EditDraft = {
  client_name: string;
  client_phone: string;
  address_line1: string;
  city: string;
  state: string;
  postal_code: string;
  property_type: string;
  gate_operator_kind: string;
  job_details: string;
  internal_notes: string;
};

const STATUS_CHIPS: { status: JobStatus; label: string; active: string; inactive: string }[] = [
  { status: "new",       label: "New Job",          active: "bg-blue-600 text-white border-blue-600",       inactive: "border-blue-200 text-blue-600 hover:bg-blue-50" },
  { status: "scheduled", label: "Sent to Tech",     active: "bg-amber-500 text-white border-amber-500",     inactive: "border-amber-200 text-amber-600 hover:bg-amber-50" },
  { status: "en_route",  label: "Approved by Tech", active: "bg-violet-600 text-white border-violet-600",   inactive: "border-violet-200 text-violet-600 hover:bg-violet-50" },
  { status: "on_site",   label: "Estimate Sent",    active: "bg-orange-500 text-white border-orange-500",   inactive: "border-orange-200 text-orange-600 hover:bg-orange-50" },
  { status: "completed", label: "Completed",        active: "bg-emerald-600 text-white border-emerald-600", inactive: "border-emerald-200 text-emerald-600 hover:bg-emerald-50" },
  { status: "cancelled", label: "Canceled",         active: "bg-slate-500 text-white border-slate-500",     inactive: "border-slate-200 text-slate-500 hover:bg-slate-50" },
];

const STATUS_LABEL_STYLE: Record<JobStatus, { badge: string; dot: string }> = {
  new:       { badge: "bg-blue-50 text-blue-700 border border-blue-200",     dot: "bg-blue-500" },
  scheduled: { badge: "bg-amber-50 text-amber-700 border border-amber-200",   dot: "bg-amber-500" },
  en_route:  { badge: "bg-violet-50 text-violet-700 border border-violet-200",dot: "bg-violet-600" },
  on_site:   { badge: "bg-orange-50 text-orange-700 border border-orange-200",dot: "bg-orange-500" },
  completed: { badge: "bg-emerald-50 text-emerald-700 border border-emerald-200", dot: "bg-emerald-600" },
  cancelled: { badge: "bg-slate-100 text-slate-500 border border-slate-200",  dot: "bg-slate-400" },
};

function draftFromJob(job: Awaited<ReturnType<typeof getJob>>): EditDraft {
  return {
    client_name: job.client_name ?? "",
    client_phone: job.client_phone ?? "",
    address_line1: job.address_line1 ?? "",
    city: job.city ?? "",
    state: job.state ?? "",
    postal_code: job.postal_code ?? "",
    property_type: job.lead_property_type ?? "",
    gate_operator_kind: job.lead_gate_operator_kind ?? "",
    job_details: job.lead_job_details ?? job.description ?? "",
    internal_notes: job.internal_notes ?? "",
  };
}

export default function JobDetailPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [noteDraft, setNoteDraft] = useState("");
  const [magicLink, setMagicLink] = useState<{ url: string; expires_at: string } | null>(null);
  const [testResult, setTestResult] = useState<TestSendResult | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft>({
    client_name: "", client_phone: "", address_line1: "", city: "", state: "", postal_code: "",
    property_type: "", gate_operator_kind: "", job_details: "", internal_notes: "",
  });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isAsap, setIsAsap] = useState(false);
  const [localScheduled, setLocalScheduled] = useState("");
  const [notifyClient, setNotifyClient] = useState(true);

  const { data: job, isLoading } = useQuery({ queryKey: ["job", id], queryFn: () => getJob(id), enabled: Boolean(id) });
  const { data: techs } = useQuery({ queryKey: ["technicians", "all"], queryFn: () => listTechnicians() });

  useEffect(() => {
    if (job && !editing) setDraft(draftFromJob(job));
    if (job) {
      setIsAsap(job.asap ?? false);
      setLocalScheduled(job.scheduled_start ? toLocalInput(job.scheduled_start) : "");
    }
  }, [job?.id]);

  const mintMut = useMutation({
    mutationFn: () => mintJobMagicLink(id),
    onSuccess: (data) => setMagicLink({ url: data.url, expires_at: data.expires_at }),
  });
  const revokeMut = useMutation({
    mutationFn: () => revokeJobMagicLink(id),
    onSuccess: () => setMagicLink(null),
  });
  const testMut = useMutation({
    mutationFn: (event: TestEvent) => sendTestNotification(id, event),
    onSuccess: (data) => setTestResult(data),
  });
  const scheduleMut = useMutation({
    mutationFn: (start: string) => rescheduleJob(id, { scheduled_start: new Date(start).toISOString(), force: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
    },
  });
  const asapMut = useMutation({
    mutationFn: (asap: boolean) => updateJob(id, { asap } as Parameters<typeof updateJob>[1]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job", id] }),
  });

  const assignMut = useMutation({
    mutationFn: (technician_id: string) => assignJob(id, technician_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job", id] }),
  });

  const [notifyResult, setNotifyResult] = useState<string | null>(null);
  const notifyTechMut = useMutation({
    mutationFn: () => notifyTech(id),
    onSuccess: (data) => {
      const r = data.results[0];
      setNotifyResult(r?.status === "sent" ? `SMS sent to ${r.to}` : `${r?.status ?? "skipped"}${r?.error ? `: ${r.error}` : ""}`);
    },
    onError: (e: Error) => setNotifyResult(`Error: ${e.message}`),
  });
  const transitionMut = useMutation({
    mutationFn: (to: JobStatus) => transitionJob(id, to, undefined, notifyClient),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
  const approveMut = useMutation({
    mutationFn: async () => {
      await transitionJob(id, "scheduled", undefined, notifyClient);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (e: Error) => setSaveError(e.message),
  });
  const noteMut = useMutation({
    mutationFn: (note: string) => addJobNote(id, note),
    onSuccess: () => { setNoteDraft(""); qc.invalidateQueries({ queryKey: ["job", id] }); },
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!job) return;
      setSaveError(null);
      const calls: Promise<unknown>[] = [
        updateJob(id, {
          address_line1: draft.address_line1 || null,
          city: draft.city || null,
          state: draft.state || null,
          postal_code: draft.postal_code || null,
          internal_notes: draft.internal_notes || null,
          description: job.lead_id ? job.description : (draft.job_details || null),
        } as Parameters<typeof updateJob>[1]),
        updateClient(job.client_id, { name: draft.client_name || null, phone_e164: draft.client_phone || undefined }),
      ];
      if (job.lead_id) {
        calls.push(updateLead(job.lead_id, {
          gate_operator_kind: draft.gate_operator_kind || undefined,
          job_details: draft.job_details || undefined,
          property_type: (draft.property_type as "residential" | "commercial") || null,
        }));
      }
      await Promise.all(calls);
    },
    onSuccess: () => {
      setEditing(false);
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ["job", id] });
    },
    onError: (e: Error) => setSaveError(e.message),
  });

  if (isLoading || !job) return <div className="text-sm text-slate-500">Loading…</div>;

  const set = (k: keyof EditDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setDraft(p => ({ ...p, [k]: e.target.value }));

  function handleStatusClick(target: JobStatus) {
    if (!job || target === job.status) return;
    if (target === "scheduled" && job.status === "new") {
      approveMut.mutate();
    } else {
      transitionMut.mutate(target);
    }
  }

  const isPending = transitionMut.isPending || approveMut.isPending;

  return (
    <div className="space-y-4">
      <header className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-mono text-xs text-slate-500">{job.job_number}</div>
            <h1 className="text-2xl font-semibold">{job.title}</h1>
          </div>
          {job.lead_id && (
            <Link to={`/leads/${job.lead_id}`} className="btn-secondary">
              ← View Lead
            </Link>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Current status label */}
          {(() => {
            const chip = STATUS_CHIPS.find(c => c.status === job.status)!;
            const style = STATUS_LABEL_STYLE[job.status];
            return (
              <span className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium ${style.badge}`}>
                <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${style.dot}`} />
                {isPending ? "Updating…" : chip.label}
              </span>
            );
          })()}
          {/* Change status dropdown */}
          <select
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm focus:border-brand-400 focus:outline-none disabled:opacity-50"
            value=""
            disabled={isPending}
            onChange={(e) => {
              const target = e.target.value as JobStatus;
              if (target) handleStatusClick(target);
              e.currentTarget.value = "";
            }}
          >
            <option value="">Change status…</option>
            {STATUS_CHIPS.filter(c => c.status !== job.status).map(({ status, label }) => (
              <option key={status} value={status}>{label}</option>
            ))}
          </select>
          {/* SMS to client toggle */}
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-600 select-none">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 accent-brand-600"
              checked={notifyClient}
              onChange={(e) => setNotifyClient(e.target.checked)}
            />
            Send SMS to client
          </label>
        </div>
      </header>

      {transitionMut.isError && (
        <div className="card border border-red-200 bg-red-50 text-sm text-red-700">{(transitionMut.error as Error).message}</div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* ── Details card ── */}
        <section className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase text-slate-500">{t("jobs.section.details")}</h2>
            {!editing ? (
              <button type="button" className="btn-secondary px-3 py-1 text-xs" onClick={() => { setDraft(draftFromJob(job)); setEditing(true); }}>
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button type="button" className="btn-secondary px-3 py-1 text-xs" onClick={() => { setEditing(false); setSaveError(null); setDraft(draftFromJob(job)); }}>
                  Cancel
                </button>
                <button type="button" className="btn-primary px-3 py-1 text-xs" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
                  {saveMut.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            )}
          </div>

          {saveError && <p className="text-sm text-red-600">{saveError}</p>}

          <Field label="Name">
            {editing
              ? <input className="input" value={draft.client_name} onChange={set("client_name")} placeholder="Customer name" />
              : <Val v={job.client_name} />}
          </Field>

          <Field label="Phone">
            {editing
              ? <input className="input" value={draft.client_phone} onChange={set("client_phone")} placeholder="+1 555 000 0000" />
              : <Val v={job.client_phone} />}
          </Field>

          <Field label="Property Type">
            {editing
              ? (
                <select className="input" value={draft.property_type} onChange={set("property_type")}>
                  <option value="">— Select —</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
              )
              : <Val v={job.lead_property_type ? job.lead_property_type.charAt(0).toUpperCase() + job.lead_property_type.slice(1) : null} />}
          </Field>

          <div className="text-sm">
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">Address</div>
            {editing ? (
              <div className="space-y-2">
                <input className="input" placeholder="Street address" value={draft.address_line1} onChange={set("address_line1")} />
                <div className="grid grid-cols-3 gap-2">
                  <input className="input" placeholder="City" value={draft.city} onChange={set("city")} />
                  <input className="input" placeholder="State" value={draft.state} onChange={set("state")} />
                  <input className="input" placeholder="ZIP" value={draft.postal_code} onChange={set("postal_code")} />
                </div>
              </div>
            ) : (
              <>
                {[
                  job.address_line1,
                  job.city && job.state
                    ? `${job.city}, ${job.state}${job.postal_code ? " " + job.postal_code : ""}`.trim()
                    : (job.city || job.state || null),
                ].filter(Boolean).map((line, i) => <div key={i} className="text-slate-800">{line}</div>)}
                {!job.address_line1 && !job.city && <div className="text-slate-400">—</div>}
              </>
            )}
          </div>

          <div className="text-sm">
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">Scheduled Time</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (isAsap) { setIsAsap(false); asapMut.mutate(false); }
                  else { setIsAsap(true); setLocalScheduled(""); asapMut.mutate(true); }
                }}
                className={`shrink-0 rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                  isAsap
                    ? "border-amber-400 bg-amber-100 text-amber-800"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                ⚡ ASAP
              </button>
              <input
                type="datetime-local"
                className={`input flex-1 ${isAsap ? "opacity-40" : ""}`}
                disabled={isAsap}
                value={isAsap ? "" : localScheduled}
                onChange={(e) => setLocalScheduled(e.target.value)}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  if (job.scheduled_start && toLocalInput(job.scheduled_start) === v && !isAsap) return;
                  setIsAsap(false);
                  asapMut.mutate(false);
                  scheduleMut.mutate(v);
                }}
              />
            </div>
            {scheduleMut.isError && <div className="mt-1 text-xs text-red-600">{(scheduleMut.error as Error).message}</div>}
          </div>

          <Field label="Gate & Operator">
            {editing
              ? <input className="input" placeholder="e.g. LiftMaster on swing gate" value={draft.gate_operator_kind} onChange={set("gate_operator_kind")} />
              : <Val v={job.lead_gate_operator_kind} />}
          </Field>

          <Field label="Job Details">
            {editing
              ? <textarea className="input" rows={3} placeholder="Describe the issue or work needed…" value={draft.job_details} onChange={set("job_details")} />
              : <Val v={job.lead_job_details ?? job.description} />}
          </Field>

          <Field label="Notes">
            {editing
              ? <textarea className="input" rows={2} placeholder="Internal notes…" value={draft.internal_notes} onChange={set("internal_notes")} />
              : <Val v={job.internal_notes} />}
          </Field>
        </section>

        {/* ── Assignment card ── */}
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold uppercase text-slate-500">{t("jobs.section.assignment")}</h2>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">{t("jobs.label.technician")}</span>
            <select className="input" value={job.technician_id ?? ""} onChange={(e) => e.target.value && assignMut.mutate(e.target.value)}>
              <option value="" disabled>—</option>
              {techs?.items.map((tech) => (
                <option key={tech.id} value={tech.user_id}>{tech.user_full_name ?? tech.user_email}</option>
              ))}
            </select>
          </label>
          {assignMut.isError && <div className="text-sm text-red-600">{(assignMut.error as Error).message}</div>}
          {job.technician_id && (
            <div className="pt-1">
              <button
                type="button"
                className="btn-secondary text-sm"
                disabled={notifyTechMut.isPending}
                onClick={() => { setNotifyResult(null); notifyTechMut.mutate(); }}
              >
                {notifyTechMut.isPending ? "Sending…" : "📱 Send SMS to technician"}
              </button>
              {notifyResult && (
                <p className={`mt-1 text-xs ${notifyResult.startsWith("SMS sent") ? "text-emerald-600" : "text-red-600"}`}>
                  {notifyResult}
                </p>
              )}
            </div>
          )}
        </section>
      </div>

      {/* ── Customer Link ── */}
      <section className="card space-y-3">
        <h2 className="text-sm font-semibold uppercase text-slate-500">{t("jobs.section.customerLink")}</h2>
        <p className="text-sm text-slate-600">{t("jobs.customerLink.description")}</p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-primary" disabled={mintMut.isPending} onClick={() => mintMut.mutate()}>
            {magicLink ? t("jobs.customerLink.rotate") : t("jobs.customerLink.mint")}
          </button>
          {magicLink && (
            <button type="button" className="btn-secondary" disabled={revokeMut.isPending} onClick={() => revokeMut.mutate()}>
              {t("jobs.customerLink.revoke")}
            </button>
          )}
        </div>
        {magicLink && (
          <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm">
            <div className="mb-1 text-xs uppercase tracking-wide text-emerald-700">{t("jobs.customerLink.shareThis")}</div>
            <div className="flex items-center gap-2">
              <input className="input flex-1 font-mono text-xs" readOnly value={magicLink.url} onFocus={(e) => e.currentTarget.select()} />
              <button type="button" className="btn-secondary" onClick={() => navigator.clipboard.writeText(magicLink.url)}>
                {t("jobs.customerLink.copy")}
              </button>
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {t("jobs.customerLink.expires")}: {new Date(magicLink.expires_at).toLocaleString()}
            </div>
          </div>
        )}
        <div className="border-t pt-3">
          <div className="mb-2 text-xs uppercase tracking-wide text-slate-500">{t("jobs.customerLink.sendTest")}</div>
          <div className="flex flex-wrap gap-2">
            {(["scheduled", "en_route", "on_site", "completed"] as TestEvent[]).map((ev) => (
              <button key={ev} type="button" className="btn-secondary text-xs" disabled={testMut.isPending} onClick={() => testMut.mutate(ev)}>
                {t(`jobs.status.${ev}`)}
              </button>
            ))}
          </div>
          {testResult && (
            <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs">
              <div className="font-semibold">{t(`jobs.status.${testResult.event}`)}:</div>
              {testResult.results.map((r, i) => (
                <div key={i} className={r.status === "sent" ? "text-emerald-700" : r.status === "skipped" ? "text-slate-500" : "text-red-700"}>
                  {r.channel.toUpperCase()} → {r.to || "(no address)"} — {r.status}{r.error ? ` (${r.error})` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Photos (booking widget uploads) ── */}
      <PhotosSection jobId={job.id} />

      {/* ── Timeline ── */}
      <section className="card space-y-3">
        <h2 className="text-sm font-semibold uppercase text-slate-500">{t("jobs.section.timeline")}</h2>
        <div className="flex gap-2">
          <input className="input flex-1" placeholder={t("jobs.notePlaceholder") ?? ""} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} />
          <button type="button" className="btn-primary" disabled={!noteDraft.trim() || noteMut.isPending} onClick={() => noteMut.mutate(noteDraft.trim())}>
            {t("jobs.addNote")}
          </button>
        </div>
        <ol className="space-y-2 border-s-2 border-slate-200 ps-4">
          {job.events.length === 0 && <li className="text-xs text-slate-400">{t("jobs.timeline.empty")}</li>}
          {job.events.map((e) => (
            <li key={e.id} className="text-sm">
              <span className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</span>
              <span className="ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600">{e.event_type}</span>
              <div className="text-slate-700">{renderEventPayload(e.event_type, e.payload)}</div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function PhotosSection({ jobId }: { jobId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["job-attachments", jobId],
    queryFn: () => listJobAttachments(jobId),
  });
  const items = data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <section className="card space-y-3">
      <h2 className="text-sm font-semibold uppercase text-slate-500">{t("jobs.section.photos")}</h2>
      <div className="flex flex-wrap gap-3">
        {items.map((a) => (
          <PhotoThumb key={a.id} jobId={jobId} attachment={a} />
        ))}
      </div>
    </section>
  );
}

function PhotoThumb({ jobId, attachment }: { jobId: string; attachment: JobAttachment }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    fetchJobAttachmentBlobUrl(jobId, attachment.id)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [jobId, attachment.id]);

  if (failed) return <div className="flex h-28 w-28 items-center justify-center rounded border border-slate-200 text-xs text-slate-400">⚠</div>;
  if (!url) return <div className="h-28 w-28 animate-pulse rounded border border-slate-200 bg-slate-100" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" title={attachment.original_filename ?? "photo"}>
      <img
        src={url}
        alt={attachment.original_filename ?? "job photo"}
        className="h-28 w-28 rounded border border-slate-200 object-cover transition hover:opacity-80"
      />
    </a>
  );
}

function renderEventPayload(type: string, payload: Record<string, unknown>): string {
  if (type === "status_change") return `${payload.from} → ${payload.to}${payload.note ? ` — ${payload.note}` : ""}`;
  if (type === "assignment") return `→ ${payload.to}`;
  if (type === "note") return String(payload.note ?? "");
  return JSON.stringify(payload);
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="text-sm">
      <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">{label}</div>
      {children}
    </div>
  );
}

function Val({ v }: { v: string | null | undefined }) {
  return <div className="text-slate-800">{v || <span className="text-slate-400">—</span>}</div>;
}
